'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCustomerOrders } from '@eliadmin/core/src/api/customers/customers-api';
import { maybeArrayUnwrap, proformApiQuery } from '@eliadmin/core/src/api/proform/proform-api';
import { ProfileOrderItem } from '@eliadmin/core/src/components/profile/profile-order-item';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

export default function DemandsPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const authToken = useCustomerStore(s => s.me!.authToken);
  const customer = useCustomerStore(s => s.me!.customer);
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const pathname = usePathname();

  const currentYear = new Date().getFullYear();
  const selectedYear = useMemo(() => {
    const yearFromParams = searchParams.get('year');
    if (!yearFromParams) return currentYear.toString();

    const parsedYear = parseInt(yearFromParams, 10);
    if (isNaN(parsedYear) || parsedYear < 2005 || parsedYear > currentYear) {
      return currentYear.toString();
    }

    return parsedYear.toString();
  }, [searchParams, currentYear]);

  const handleYearChange = (year: string) => {
    const parsedYear = parseInt(year, 10);
    if (!isNaN(parsedYear) && parsedYear >= 2005 && parsedYear <= currentYear) {
      router.push(`${pathname}?year=${year}`, { scroll: false });
    }
  };

  const years = useMemo(() => {
    const yearArray = [];
    for (let year = 2000; year <= currentYear; year++) {
      yearArray.push(year.toString());
    }
    return yearArray.reverse();
  }, [currentYear]);

  const eliOrders = useQuery({
    queryKey: [CacheKey.ORDERS, authToken, locale, selectedYear],
    queryFn: () => fetchCustomerOrders({ locale, authToken, query: { page: 0, results: 1000 } }),
  });

  const smakoOrders = useQuery({
    queryKey: ['smako-orders', customer.email, selectedYear],
    queryFn: () =>
      proformApiQuery('ELI_GET_ORDERS', {
        e_mail: customer.email,
        od_data: `${selectedYear}-01-01`,
        do_data:
          selectedYear === String(currentYear)
            ? new Date().toISOString().split('T')[0]
            : `${selectedYear}-12-31`,
      }),
  });

  const combinedOrders = useMemo(() => {
    if (!smakoOrders.data || !eliOrders.data) return [];

    const smakoOrdersArray = maybeArrayUnwrap(smakoOrders.data.root.orders?.order ?? [])
      .filter(order => order !== undefined)
      .filter(order => order.druh_dokladu === '10');
    const eliOrdersArray = eliOrders.data.data;

    return smakoOrdersArray.map(smakoOrder => {
      const eliOrder = eliOrdersArray.find(eliOrder => eliOrder.identifier === smakoOrder?.doklad);
      return {
        smakoOrder,
        eliOrder,
      };
    });
  }, [smakoOrders.data, eliOrders.data]);

  if (eliOrders.isLoading || smakoOrders.isLoading) {
    return <GenericLoader />;
  }

  return (
    <div className="flex flex-col gap-4 @container">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <h2 className="text-2xl font-bold">{t('text1239')}</h2>
        <Select value={selectedYear} onValueChange={handleYearChange}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px] overflow-x-auto">
            {years.map(year => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* header */}
      {combinedOrders.length > 0 && (
        <div className="hidden flex-row items-center gap-4 p-4 @xl:flex">
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text1229')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text108')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text1234')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text132')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground"></span>
        </div>
      )}
      {/* content */}
      <div className="-mx-4 flex flex-col @xl:mx-0 @xl:gap-4">
        {combinedOrders.map(order => (
          <ProfileOrderItem
            key={order.smakoOrder.objednavka}
            order={order.smakoOrder}
            eliOrder={order.eliOrder}
          />
        ))}
      </div>

      {!combinedOrders.length && (
        <p className="text-bold text-sm text-muted-foreground">{t('text445')}</p>
      )}
    </div>
  );
}
